import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
  Box, Button, FormControl, InputLabel, Select, MenuItem, TextField, Divider, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Swal from "sweetalert2";
import moment from "moment-timezone";
import { GetSalesDocumentById, UpdateSalesDocument, GetSalesDocumentPdfBlobUrl } from "api/salesDocument";
import { STATUS_BY_DOC_TYPE } from "./statusOptions";

const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const DOC_TYPE_LABEL = { receipt: "docTypeReceipt", quotation: "docTypeQuotation", invoice: "docTypeInvoice" };
// LAK has no sub-unit in practice (smallest banknote is ₭1,000), so it's
// shown with no decimal places — THB/USD keep 2.
const money = (n, c) => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, c === "LAK" ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2 })}`;

// `onDuplicate(doc)` — optional; when given, shows a "Duplicate" action that
// hands the full loaded document to the parent (which switches to the Create
// tab and prefills it — see scenes/salesDocuments/index.jsx). All doc types
// can be duplicated into any doc type; CreateTab just seeds the form, it
// doesn't restrict which docType the staff picks next.
const DocumentDetailDialog = ({ open, docId, onClose, onChanged, onDuplicate }) => {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (open && docId) {
      setIsLoading(true);
      GetSalesDocumentById(docId).then((res) => {
        setDoc(res.success ? res.data : null);
        setStatus(res.success ? res.data.status : "");
        setIsLoading(false);
      });
    } else {
      setDoc(null);
    }
  }, [open, docId]);

  const handleSaveStatus = () => {
    setIsSaving(true);
    UpdateSalesDocument(docId, { status }).then((res) => {
      setIsSaving(false);
      if (res.success) {
        setDoc(res.data);
        onChanged?.();
        Swal.fire({ icon: "success", text: t("salesDocuments.statusSaved"), timer: 1800, toast: true, position: "top-right", showConfirmButton: false });
      } else {
        Swal.fire({ icon: "error", text: res.message });
      }
    });
  };

  const handlePrint = () => {
    setIsPrinting(true);
    GetSalesDocumentPdfBlobUrl(docId).then((res) => {
      setIsPrinting(false);
      if (res.success) window.open(res.url, "_blank");
      else Swal.fire({ icon: "error", text: res.message });
    });
  };

  // A blob: URL opened in the browser's PDF viewer saves under a random UUID, so
  // downloading goes through an <a download> that carries the doc-number filename.
  const handleDownload = () => {
    setIsDownloading(true);
    GetSalesDocumentPdfBlobUrl(docId).then((res) => {
      setIsDownloading(false);
      if (!res.success) {
        Swal.fire({ icon: "error", text: res.message });
        return;
      }
      const a = document.createElement("a");
      a.href = res.url;
      a.download = `Winner_${doc.docNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(res.url), 1000);
    });
  };

  const handleDuplicate = () => {
    if (!doc) return;
    onDuplicate?.(doc);
    Swal.fire({ icon: "success", text: t("salesDocuments.duplicateLoaded"), timer: 2500, toast: true, position: "top-right", showConfirmButton: false });
  };

  const statusOptions = doc ? STATUS_BY_DOC_TYPE[doc.docType] || [] : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" color="secondary">{t("salesDocuments.detailTitle")}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading || !doc ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress color="secondary" /></Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{doc.docNumber}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`salesDocuments.${DOC_TYPE_LABEL[doc.docType]}`)} · {moment(doc.date).tz("Asia/Vientiane").format("DD/MM/YYYY")}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2"><strong>{t("salesDocuments.customerName")}:</strong> {doc.customer?.name}</Typography>
            {doc.customer?.address && <Typography variant="body2"><strong>{t("salesDocuments.customerAddress")}:</strong> {doc.customer.address}</Typography>}
            {doc.customer?.contact && <Typography variant="body2"><strong>{t("salesDocuments.customerContact")}:</strong> {doc.customer.contact}</Typography>}

            <Divider sx={{ my: 2 }} />

            {doc.items.map((item, idx) => (
              <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", py: "3px", fontSize: "0.875rem" }}>
                <span>{item.description} × {item.qty}</span>
                <span>{money(item.amount, doc.currency)}</span>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography color="secondary" fontWeight={700}>{t("salesDocuments.totalAmount")}</Typography>
              <Typography color="secondary" fontWeight={700}>{money(doc.totalAmount, doc.currency)}</Typography>
            </Box>
            {doc.currency === "THB" && doc.totalLAK != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">≈ LAK</Typography>
                <Typography variant="body2" color="text.secondary">{money(doc.totalLAK, "LAK")}</Typography>
              </Box>
            )}
            {doc.currency === "THB" && doc.totalUSD != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">≈ USD</Typography>
                <Typography variant="body2" color="text.secondary">{money(doc.totalUSD, "USD")}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <FormControl size="small" fullWidth>
              <InputLabel>{t("salesDocuments.historyColumnStatus")}</InputLabel>
              <Select label={t("salesDocuments.historyColumnStatus")} value={status} onChange={(e) => setStatus(e.target.value)}>
                {statusOptions.map((s) => (
                  <MenuItem key={s} value={s}>{t(`salesDocuments.status${s}`, s)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {doc.note && (
              <TextField
                fullWidth multiline minRows={2} sx={{ mt: 2 }}
                label={t("salesDocuments.note")}
                value={doc.note}
                InputProps={{ readOnly: true }}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <Button variant="outlined" color="secondary" startIcon={<PrintIcon />} onClick={handlePrint} disabled={isPrinting || !doc}>
          {t("salesDocuments.viewPdf")}
        </Button>
        <Button variant="outlined" color="secondary" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={isDownloading || !doc}>
          {t("salesDocuments.downloadPdf")}
        </Button>
        {onDuplicate && (
          <Button variant="outlined" color="secondary" startIcon={<ContentCopyIcon />} onClick={handleDuplicate} disabled={!doc}>
            {t("salesDocuments.duplicate")}
          </Button>
        )}
        <Button
          variant="contained" color="secondary"
          onClick={handleSaveStatus}
          disabled={isSaving || !doc || status === doc?.status}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : t("salesDocuments.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentDetailDialog;
